import {
  ACC,
  ACC_D,
  ACC_L,
  BRASS,
  DARK,
  DARK_L,
  DETAIL_RES,
  GLOW,
  GLOW_HI,
  LENS,
  LENS_HI,
  LIGHT,
  METAL,
  METAL_D,
  METAL_L,
  ModelBuilder,
  PRI,
  PRI_D,
  RED,
  RUBBER,
  SEC,
  SEC_D,
  SEC_L,
  WHITE,
  type WeaponPartName,
} from './builder';

export type V3 = [number, number, number];

/** How the view model holds and animates a weapon. */
export interface WeaponRig {
  /** Holding pose family. */
  hold: 'pistol' | 'rifle' | 'heavy' | 'shoulder' | 'low' | 'melee';
  /** Reload animation. */
  reload: 'none' | 'mag' | 'shells' | 'break' | 'bolt' | 'revolver' | 'launcher' | 'cell' | 'belt' | 'crossbow';
  /** Per-shot mechanical action. */
  action: 'none' | 'pump' | 'bolt' | 'slide' | 'cyl' | 'spin';
  /** Melee swing arc (melee weapons). */
  swing: 'chop' | 'slash' | 'bash' | 'thrust' | 'swipe';
  /** First-person size multiplier relative to the hold family's default. */
  vmScale: number;
}

export interface PartMeta {
  /** Rotation pivot / attachment point in voxel coordinates. */
  pivot: V3;
  /** Direction the part leaves the weapon during reloads (model space, unnormalized). */
  dir?: V3;
  /** Rotation axis for spinning parts. */
  axis?: 'x' | 'y' | 'z';
}

/**
 * Region of a built-in optic (red dot, scope) that a fitted scope replaces: every voxel at or above `y0` with z in [z0, z1)
 * (only those whose color is in `only`, when given) is removed before the new scope is mounted.
 */
export interface StockOptic {
  y0: number;
  z0: number;
  z1: number;
  only?: number[];
}

/** Where a fitted sight mounts: the receiver top height and the rear end of the mount (a design states it when the guess is off). */
export interface MountSpec {
  /** Height (voxel row) of the highest solid row of the receiver the rail sits on. */
  y?: number;
  /** z (exclusive) where the sight's rear end sits. Default: two voxels behind the sight point. */
  z1?: number;
}

export interface DesignMeta {
  /** Built-in optic that a fitted scope replaces (undefined: the weapon has plain iron sights). */
  stock?: StockOptic;
  /** Where a fitted sight sits on the weapon (defaults are derived from the sight point and the receiver top). */
  mount?: MountSpec;
  grip: V3;
  muzzle: V3;
  /** Support-hand point (null = one-handed). */
  fore: V3 | null;
  /** A point on the sight line (the eye looks along -Z through it when aiming). */
  sight: V3;
  rig: WeaponRig;
  parts?: Partial<Record<WeaponPartName, PartMeta>>;
}

export interface Design {
  size: V3;
  /**
   * Draws the gun into `b` (2.5 cm voxels). `f` is the detail layer: same extent in half-size voxels (coordinates are gun
   * coordinates x DETAIL_RES), for hardware too small for `b` (rails, vents, handles). Optional; most designs ignore it.
   * Tag sight hardware with b.gear(true)/f.gear(true) so a fitted scope can replace exactly that.
   */
  build(b: ModelBuilder, f: ModelBuilder): DesignMeta;
}

const rig = (hold: WeaponRig['hold'], reload: WeaponRig['reload'], action: WeaponRig['action'] = 'none', swing: WeaponRig['swing'] = 'bash', vmScale = 1): WeaponRig => ({
  hold,
  reload,
  action,
  swing,
  vmScale,
});

/** Pistol grip leaning back, with a rubber texture band. */
function pistolGrip(b: ModelBuilder, yTop: number, yBot: number, z0: number, len: number, v: number, slope = 0.5): void {
  b.slant(2, yTop, yBot, z0, len, slope, v);
  for (let y = yTop - 2; y > yBot; y -= 2) {
    const s = Math.floor((yTop - 1 - y) * slope);
    b.sides(Math.round(b.cx - 1), y, y + 1, z0 + s + 1, z0 + s + len - 1, RUBBER);
  }
}

/** Trigger guard loop under a receiver whose bottom is at height y. */
function triggerGuard(b: ModelBuilder, y: number, z0: number, z1: number): void {
  b.c(2, y - 2, y - 1, z0, z1, METAL_D);
  b.c(2, y - 1, y, z0, z0 + 1, METAL_D);
  b.c(2, y - 1, y, z0 + 2, z0 + 3, DARK_L);
}

/** Straight-walled or curved box magazine drawn into the 'mag' part. */
function magazine(b: ModelBuilder, yTop: number, yBot: number, z0: number, len: number, curve: number, v: number, base = METAL_D, w = 2): void {
  b.part('mag');
  for (let y = yTop - 1; y >= yBot; y--) {
    const s = Math.floor((yTop - 1 - y) * curve);
    b.c(w, y, y + 1, z0 - s, z0 - s + len, y === yBot ? base : v);
  }
  b.part(null);
}

// Detail-layer helpers (the design's fine hardware layer, half-size voxels) ----------------------------------
const D = DETAIL_RES;
/** Palette slots camos never recolor: the only ones the detail layer uses, so every camo leaves it alone. */

/**
 * Raised mortar lines (a brick bond: courses 3 cells tall, joints every 8 cells and staggered per course) on the two outer side
 * faces of the body voxels of the given colors in a gun-space region. `xl`/`xr` are the outermost body columns on each side.
 */
function brickBond(b: ModelBuilder, f: ModelBuilder, xl: number, xr: number, y0: number, y1: number, z0: number, z1: number, match: number[], v = METAL_D): void {
  for (let hy = y0 * D; hy < y1 * D; hy++) {
    const course = Math.floor(hy / 3);
    for (let hz = z0 * D; hz < z1 * D; hz++) {
      if (!(hy % 3 === 0 || (hz + course * 3) % 8 === 0)) continue;
      const gy = hy >> 1;
      const gz = hz >> 1;
      if (match.includes(b.get(xl, gy, gz)) && b.get(xl - 1, gy, gz) === 0) f.set(xl * D - 1, hy, hz, v);
      if (match.includes(b.get(xr, gy, gz)) && b.get(xr + 1, gy, gz) === 0) f.set(xr * D + D, hy, hz, v);
    }
  }
}

/** Faint raised grain streaks on the outer side faces of wooden body voxels (deterministic per cell). */
function woodGrain(b: ModelBuilder, f: ModelBuilder, xl: number, xr: number, y0: number, y1: number, z0: number, z1: number, match: number[], seed: number, v = DARK_L): void {
  for (let hy = y0 * D; hy < y1 * D; hy++) {
    if ((hy * 5 + seed) % 4 !== 0) continue;
    for (let hz = z0 * D; hz < z1 * D; hz++) {
      if ((hz + hy * 3 + seed) % 7 > 3) continue;
      const gy = hy >> 1;
      const gz = hz >> 1;
      if (match.includes(b.get(xl, gy, gz)) && b.get(xl - 1, gy, gz) === 0) f.set(xl * D - 1, hy, hz, v);
      if (match.includes(b.get(xr, gy, gz)) && b.get(xr + 1, gy, gz) === 0) f.set(xr * D + D, hy, hz, v);
    }
  }
}

/** A thin rectangular band round a body box [x0,x1) x [y0,y1) (gun voxels) at fine-layer z `hz`, `t` cells thick. */
function band(f: ModelBuilder, x0: number, x1: number, y0: number, y1: number, hz: number, t: number, v: number): void {
  for (let z = hz; z < hz + t; z++)
    for (let hy = y0 * D - 1; hy <= y1 * D; hy++)
      for (let hx = x0 * D - 1; hx <= x1 * D; hx++) if (hy === y0 * D - 1 || hy === y1 * D || hx === x0 * D - 1 || hx === x1 * D) f.set(hx, hy, z, v);
}

/** An outline rectangle on a side plane (fine-layer x = hx), covering fine cells hy0..hy1 x hz0..hz1 inclusive. */
function sideFrame(f: ModelBuilder, hx: number, hy0: number, hy1: number, hz0: number, hz1: number, v: number): void {
  for (let hy = hy0; hy <= hy1; hy++)
    for (let hz = hz0; hz <= hz1; hz++) if (hy === hy0 || hy === hy1 || hz === hz0 || hz === hz1) f.set(hx, hy, hz, v);
}

/** Raised ribs on both sides of a magazine drawn with magazine() (same arguments), into the fine layer's 'mag' part. */
function magRibs(f: ModelBuilder, yTop: number, yBot: number, z0: number, len: number, curve: number, w: number, v: number): void {
  const xg = Math.round(f.cx / D - w / 2);
  f.part('mag');
  for (let y = yTop - 1; y >= yBot; y--) {
    if ((yTop - 1 - y) % 2) continue;
    const zs = z0 - Math.floor((yTop - 1 - y) * curve);
    for (let hz = zs * D + 1; hz < (zs + len) * D - 1; hz++) f.set(xg * D - 1, y * D + 1, hz, v).set((xg + w) * D, y * D + 1, hz, v);
  }
  f.part(null);
}

/** A trigger blade in the open cell (gun y, z) of a trigger guard. */
function triggerBlade(f: ModelBuilder, y: number, z: number): void {
  f.c(2, y * D, y * D + 2, z * D, z * D + 1, METAL_D);
}

/**
 * Low-profile iron sights in the detail layer (half-size voxels), tagged as sight gear so a fitted scope replaces them: a front
 * post with a bright tip and a rear sight of two posts around a notch. The sight line passes over the front post and through the
 * notch. `lineY` is its height in gun voxels; `frontY` / `rearY` are the gun-voxel rows the posts stand on (the top surface of the
 * body under each) and `zFront` / `zRear` their gun-voxel z. Factory sights are always thin irons like these, never a boxy optic:
 * boxes on the receiver read as scopes, and scopes are things a player buys and fits.
 */
function ironSights(f: ModelBuilder, lineY: number, frontY: number, zFront: number, rearY: number, zRear: number, tip: number = METAL_L, post: number = DARK): void {
  const cx = Math.round(f.cx);
  const line = Math.round(lineY * D);
  f.gear(true);
  const fz = zFront * D;
  for (let y = frontY * D; y < line; y++) f.box(cx - 1, y, fz, cx + 1, y + 1, fz + 2, y === line - 1 ? tip : post);
  const rz = zRear * D;
  for (let y = rearY * D; y < line + 2; y++) f.box(cx - 2, y, rz, cx - 1, y + 1, rz + 2, post).box(cx + 1, y, rz, cx + 2, y + 1, rz + 2, post);
  f.box(cx - 1, rearY * D, rz, cx + 1, rearY * D + 1, rz + 2, post);
  f.gear(false);
}

// Weapon designs, keyed by weapon id (archetype defaults map to one of these). -----------------

export const DESIGNS: Record<string, Design> = {
  // Rifles ------------------------------------------------------------------------------------
  brick_rifle: {
    size: [6, 17, 34],
    build(b, f) {
      // Muzzle: a castle-crenellated brake (merlon, notch, merlon) around a dark bore, then a collar and the barrel.
      b.c(4, 9, 12, 0, 3, DARK).cc(4, 11, 12, 1, 2).cc(2, 10, 11, 0, 1);
      b.c(2, 10, 11, 1, 2, RUBBER);
      b.c(4, 9, 13, 3, 4, METAL_D);
      b.c(2, 10, 12, 4, 18, METAL);
      // Front sight (sight gear): base block, post and two protective ears.
      b.gear(true).c(2, 12, 13, 5, 8, METAL_D).c(2, 13, 15, 6, 7, DARK).gear(false);
      f.gear(true).box(3, 26, 11, 4, 29, 14, METAL_D).box(8, 26, 11, 9, 29, 14, METAL_D).gear(false);
      // Under-barrel lug and the handguard: brown wood with a brick bond of fine mortar lines.
      b.c(2, 8, 10, 6, 8, METAL_D);
      b.c(4, 8, 13, 9, 17, SEC).c(2, 7, 8, 9, 17, SEC);
      // Receiver, ejection port and charging handle, gold-trimmed side panels.
      b.c(4, 6, 13, 16, 26, PRI);
      b.box(4, 10, 19, 5, 12, 23, DARK).box(5, 11, 17, 6, 12, 19, METAL_D);
      b.box(0, 9, 23, 1, 10, 25, DARK_L);
      for (const x of [1, 4]) {
        b.paint(x, 8, 18, x + 1, 10, 23, ACC);
        b.recolor(x, 9, 20, ACC_D).recolor(x, 8, 18, ACC_D).recolor(x, 8, 22, ACC_D);
      }
      // One-piece top rail (sight gear: a fitted sight brings its own) with fine teeth, and the rear sight.
      b.gear(true).c(2, 13, 14, 9, 25, PRI);
      b.c(4, 14, 15, 22, 25, METAL_D).box(1, 15, 23, 2, 16, 25, DARK).box(4, 15, 23, 5, 16, 25, DARK).gear(false);
      f.gear(true);
      for (let hz = 18; hz < 50; hz += 2) f.c(2, 28, 29, hz, hz + 1, METAL_D);
      f.gear(false);
      magazine(b, 6, 0, 18, 3, 0.5, PRI_D);
      triggerGuard(b, 6, 20, 23);
      pistolGrip(b, 6, 0, 23, 3, SEC_D);
      for (let z = 26; z < 34; z++) {
        const top = z < 31 ? 13 : 12;
        const bot = 6 - Math.floor((z - 26) * 0.45);
        b.c(4, bot, top, z, z + 1, z === 33 ? DARK : SEC);
      }
      b.sides(1, 9, 10, 27, 32, ACC);
      // --- Fine hardware ---
      brickBond(b, f, 1, 4, 7, 13, 9, 17, [SEC, SEC_L, SEC_D]);
      brickBond(b, f, 1, 4, 3, 13, 26, 33, [SEC, SEC_L, SEC_D]);
      band(f, 2, 4, 10, 12, 16, 2, BRASS);
      sideFrame(f, 10, 19, 24, 37, 46, METAL_D);
      magRibs(f, 6, 0, 18, 3, 0.5, 2, METAL_D);
      triggerBlade(f, 5, 21);
      for (let y = 1; y < 5; y++) {
        const s0 = Math.floor((5 - y) * 0.5);
        for (let hy = y * D; hy < y * D + D; hy++)
          for (let hz = (23 + s0) * D + 1; hz < (23 + s0 + 3) * D - 1; hz++) if ((hy + hz) % 2 === 0) f.set(3, hy, hz, DARK_L).set(8, hy, hz, DARK_L);
      }
      b.bevel();
      f.bevel();
      return {
        mount: { y: 12 },
        grip: [3, 3, 25.5],
        muzzle: [3, 11, 0],
        fore: [3, 7, 13],
        sight: [3, 15, 24],
        rig: rig('rifle', 'mag'),
        parts: { mag: { pivot: [3, 6, 19.5], dir: [0, -1, 0.35] } },
      };
    },
  },

  tempest_ar: {
    size: [6, 19, 35],
    build(b, f) {
      b.c(2, 11, 13, 0, 3, DARK).cc(2, 12, 13, 1, 2);
      b.c(4, 10, 14, 2, 3, METAL_D);
      b.c(2, 11, 13, 3, 20, METAL);
      b.c(4, 9, 14, 7, 20, PRI);
      for (const z of [9, 12, 15]) b.symBox(1, 10, z, 2, 12, z + 2, DARK);
      b.c(4, 9, 14, 7, 8, ACC);
      b.rail(14, 7, 28);
      b.slant(2, 9, 5, 12, 2, -0.35, SEC).c(2, 5, 6, 11, 13, ACC);
      b.c(4, 10, 14, 19, 28, PRI).c(4, 7, 10, 19, 28, SEC);
      b.box(4, 11, 21, 5, 13, 25, DARK).box(5, 12, 20, 6, 13, 22, METAL_D);
      b.box(0, 9, 24, 1, 10, 26, ACC);
      // Factory sights: thin irons on the rail (the built-in red dot is gone: optics are something a player buys and fits).
      ironSights(f, 16.5, 15, 8, 15, 23, METAL_L);
      magazine(b, 7, 0, 20, 3, -0.2, DARK, ACC);
      triggerGuard(b, 7, 22, 25);
      pistolGrip(b, 7, 1, 25, 3, DARK, 0.45);
      b.c(2, 11, 13, 28, 32, DARK_L);
      b.c(4, 8, 14, 31, 35, SEC).carve(1, 9, 32, 5, 11, 34);
      b.c(4, 7, 14, 34, 35, DARK);
      b.sides(1, 12, 13, 31, 34, ACC);
      b.bevel();
      f.bevel();
      return {
        mount: { y: 14 },
        grip: [3, 4, 26.5],
        muzzle: [3, 12, 0],
        fore: [3, 6, 12],
        sight: [3, 16.5, 23],
        rig: rig('rifle', 'mag'),
        parts: { mag: { pivot: [3, 7, 21.5], dir: [0, -1, -0.15] } },
      };
    },
  },

  // SMGs -------------------------------------------------------------------------------------
  buzz_smg: {
    size: [6, 16, 24],
    build(b) {
      b.c(2, 9, 11, 0, 2, DARK).c(2, 9, 11, 2, 6, METAL);
      b.c(4, 6, 13, 4, 18, PRI);
      b.sides(1, 7, 10, 5, 12, SEC);
      b.sides(1, 11, 12, 4, 18, SEC);
      b.rail(13, 8, 17);
      // Front post and rear sight are sight gear: a fitted sight replaces them (a post left standing shows through its window).
      b.gear(true).c(2, 13, 15, 5, 6, DARK);
      b.c(4, 13, 14, 15, 17, METAL_D).box(1, 14, 15, 2, 15, 17, DARK).box(4, 14, 15, 5, 15, 17, DARK).gear(false);
      b.box(0, 10, 9, 1, 11, 12, ACC);
      magazine(b, 6, 0, 8, 3, -0.15, DARK, ACC);
      triggerGuard(b, 6, 11, 14);
      pistolGrip(b, 6, 1, 14, 3, DARK, 0.4);
      b.c(2, 11, 13, 18, 23, SEC).c(2, 6, 8, 18, 23, SEC).c(4, 6, 13, 23, 24, DARK);
      b.bevel();
      return {
        grip: [3, 3.5, 15.5],
        muzzle: [3, 10, 0],
        fore: [3, 6, 6],
        sight: [3, 15, 16],
        // The rail on top (row 13) runs ahead of the sight: the sight stands on it, or the rail shows through its window.
        mount: { y: 13 },
        rig: rig('rifle', 'mag'),
        parts: { mag: { pivot: [3, 6, 9.5], dir: [0, -1, -0.1] } },
      };
    },
  },

  hornet_smg: {
    size: [6, 16, 22],
    build(b) {
      b.c(2, 9, 11, 0, 1, ACC).c(2, 9, 11, 1, 3, DARK).c(4, 8, 12, 3, 4, ACC);
      b.c(2, 9, 11, 4, 6, METAL);
      b.c(4, 6, 13, 5, 18, PRI).c(2, 13, 14, 6, 17, PRI);
      for (const z of [7, 11, 15]) b.c(4, 6, 13, z, z + 1, SEC).c(2, 13, 14, z, z + 1, SEC);
      b.c(4, 7, 12, 18, 19, SEC).c(2, 8, 11, 19, 20, SEC);
      b.box(4, 10, 12, 5, 12, 15, DARK);
      b.gear(true).c(2, 13, 15, 6, 7, ACC).c(4, 13, 16, 14, 16, DARK).cc(2, 14, 16, 14, 16).gear(false); // front post + rear sight (sight gear)
      triggerGuard(b, 6, 9, 12);
      b.c(4, 2, 6, 12, 16, SEC);
      b.sides(1, 3, 4, 12, 16, RUBBER);
      b.part('mag').c(2, 0, 2, 13, 15, DARK_L).c(2, 0, 1, 13, 15, ACC).part(null);
      b.bevel();
      return {
        grip: [3, 4, 14],
        muzzle: [3, 10, 0],
        fore: [3, 6, 7.5],
        sight: [3, 15, 15],
        mount: { y: 13 },
        rig: rig('rifle', 'mag'),
        parts: { mag: { pivot: [3, 2, 14], dir: [0, -1, 0] } },
      };
    },
  },

  wasp_pdw: {
    size: [6, 18, 26],
    build(b, f) {
      b.tube(3, 10, 0, 7, 1.6, DARK).tube(3, 10, 0, 1, 0.8, RUBBER);
      b.tube(3, 10, 6, 7, 2.2, ACC);
      b.c(4, 7, 13, 7, 14, PRI);
      b.sides(1, 10, 11, 7, 14, GLOW);
      b.c(4, 6, 13, 13, 22, PRI).c(4, 6, 9, 13, 22, SEC);
      b.sides(1, 10, 11, 14, 21, GLOW);
      b.box(4, 11, 15, 5, 12, 18, DARK);
      b.c(2, 13, 14, 13, 20, DARK); // a narrow top rail, not a wide cover
      // Factory sights: thin irons in the fine layer (a lit front dot), not a box on the receiver.
      ironSights(f, 15.5, 13, 9, 14, 17, GLOW);
      b.slant(2, 7, 3, 9, 2, 0, SEC).c(2, 3, 4, 9, 11, ACC);
      magazine(b, 6, 0, 14, 3, -0.2, DARK, ACC);
      triggerGuard(b, 6, 17, 20);
      pistolGrip(b, 6, 0, 19, 3, SEC, 0.4);
      b.c(2, 10, 12, 22, 25, DARK_L).c(4, 7, 13, 25, 26, SEC).c(2, 7, 8, 22, 25, DARK_L);
      b.bevel();
      f.bevel();
      return {
        grip: [3, 3, 20.5],
        muzzle: [3, 10, 0],
        fore: [3, 4.5, 10],
        mount: { y: 13 },
        sight: [3, 15.5, 17],
        rig: rig('rifle', 'mag'),
        parts: { mag: { pivot: [3, 6, 15.5], dir: [0, -1, -0.2] } },
      };
    },
  },

  // Shotguns ----------------------------------------------------------------------------------
  scattergun: {
    size: [6, 15, 37],
    build(b) {
      b.c(2, 10, 12, 0, 24, METAL).c(2, 10, 12, 0, 1, DARK);
      b.c(2, 12, 13, 1, 22, METAL_D);
      for (let z = 4; z < 22; z += 3) b.cc(2, 12, 13, z, z + 1);
      b.gear(true).c(2, 12, 13, 1, 2, BRASS).gear(false); // front bead: sight gear
      b.c(2, 7, 9, 3, 21, METAL_D).c(2, 7, 9, 2, 3, DARK);
      b.part('pump').c(4, 6, 10, 8, 17, SEC);
      for (let z = 9; z < 17; z += 2) b.symBox(1, 7, z, 2, 9, z + 1, SEC_D);
      b.part(null);
      b.c(4, 6, 13, 21, 29, PRI);
      b.box(4, 9, 23, 5, 11, 27, DARK).c(2, 5, 6, 22, 26, DARK);
      for (const z of [21, 23, 25, 27]) b.box(0, 8, z, 1, 9, z + 1, BRASS).box(0, 9, z, 1, 11, z + 1, ACC);
      triggerGuard(b, 6, 25, 28);
      pistolGrip(b, 6, 0, 28, 3, DARK, 0.45);
      for (let z = 29; z < 37; z++) {
        const top = z < 33 ? 12 : 11;
        const bot = 6 - Math.floor((z - 29) * 0.4);
        b.c(4, bot, top, z, z + 1, z === 36 ? DARK : SEC);
      }
      b.bevel();
      return {
        grip: [3, 3, 29.5],
        muzzle: [3, 11, 0],
        fore: [3, 6, 12.5],
        sight: [3, 13.2, 27],
        rig: rig('rifle', 'shells', 'pump'),
        parts: { pump: { pivot: [3, 8, 12.5], dir: [0, 0, 1] } },
      };
    },
  },

  twin_barrel: {
    size: [6, 14, 38],
    build(b) {
      b.part('break');
      b.box(0, 9, 0, 2, 11, 22, SEC).box(4, 9, 0, 6, 11, 22, SEC).c(2, 9, 11, 0, 22, METAL_D);
      b.box(0, 9, 0, 2, 11, 1, DARK).box(4, 9, 0, 6, 11, 1, DARK);
      b.c(2, 11, 12, 1, 22, METAL_L).c(2, 12, 13, 1, 2, BRASS);
      b.c(6, 8, 9, 0, 2, METAL_D);
      b.c(4, 6, 9, 8, 19, PRI);
      for (let z = 10; z < 18; z += 2) b.sym(1, 7, z, PRI_D);
      b.box(0, 9, 21, 2, 11, 22, BRASS).box(4, 9, 21, 6, 11, 22, BRASS);
      b.part(null);
      b.c(4, 6, 12, 22, 28, SEC).c(6, 7, 8, 22, 23, ACC);
      b.sides(1, 8, 11, 23, 27, ACC);
      for (const [y, z] of [[9, 24], [9, 26], [10, 25]] as const) b.sym(1, y, z, ACC_D);
      b.box(1, 12, 26, 2, 14, 27, DARK).box(4, 12, 26, 5, 14, 27, DARK).c(2, 12, 13, 25, 28, ACC);
      b.c(2, 4, 5, 23, 27, METAL_D).c(2, 5, 6, 23, 24, METAL_D).c(2, 5, 6, 24, 26, ACC);
      b.slant(2, 7, 1, 28, 3, 0.4, PRI);
      for (let z = 28; z < 38; z++) {
        const top = z < 33 ? 12 : 11;
        const bot = 6 - Math.floor((z - 28) * 0.35);
        b.c(4, bot, top, z, z + 1, z === 37 ? DARK : PRI);
      }
      b.sides(1, 9, 10, 31, 35, ACC);
      b.bevel();
      return {
        grip: [3, 3.5, 29.5],
        muzzle: [3, 10, 0],
        fore: [3, 6, 13],
        sight: [3, 12.5, 26],
        rig: rig('rifle', 'break'),
        parts: { break: { pivot: [3, 7.5, 22], axis: 'x' } },
      };
    },
  },

  auto_scatter: {
    size: [6, 17, 34],
    build(b) {
      b.c(4, 8, 12, 0, 3, DARK);
      b.symBox(1, 9, 1, 2, 11, 2, 0);
      b.c(2, 9, 11, 0, 1, RUBBER);
      b.c(2, 9, 11, 3, 20, METAL);
      b.c(4, 8, 12, 4, 14, PRI);
      for (let z = 5; z < 14; z += 2) b.symBox(1, 10, z, 2, 11, z + 1, 0);
      b.c(2, 12, 15, 4, 5, DARK);
      b.c(4, 5, 13, 13, 27, PRI);
      b.sides(1, 11, 12, 14, 26, SEC);
      b.box(4, 9, 17, 5, 11, 21, DARK);
      b.c(2, 13, 14, 15, 17, DARK_L).c(2, 13, 14, 23, 25, DARK_L).c(2, 14, 15, 15, 25, DARK_L);
      b.c(4, 15, 16, 22, 24, DARK).cc(2, 15, 16, 22, 24);
      b.part('mag').tubeX(2.5, 17, 1, 5, 3.3, SEC).tubeX(2.5, 17, 1, 5, 1.2, METAL_D).c(2, 5, 6, 16, 18, SEC_D).part(null);
      triggerGuard(b, 5, 21, 24);
      pistolGrip(b, 5, 0, 24, 3, DARK_L, 0.4);
      b.c(4, 6, 13, 27, 34, PRI).carve(1, 8, 29, 5, 10, 32);
      b.c(4, 6, 13, 33, 34, DARK);
      b.sides(1, 11, 12, 27, 33, SEC);
      b.bevel();
      return {
        grip: [3, 2.5, 25],
        muzzle: [3, 10, 0],
        fore: [3, 7.5, 9],
        sight: [3, 15.5, 23],
        rig: rig('rifle', 'mag'),
        parts: { mag: { pivot: [3, 5, 17], dir: [0, -1, 0.1] } },
      };
    },
  },

  sawed_off: {
    size: [6, 13, 20],
    build(b) {
      b.part('break');
      b.box(0, 8, 0, 2, 10, 10, SEC).box(4, 8, 0, 6, 10, 10, SEC).c(2, 8, 10, 0, 10, METAL_D);
      b.box(0, 8, 0, 2, 10, 1, DARK).box(4, 8, 0, 6, 10, 1, DARK);
      b.c(6, 7, 8, 0, 1, ACC).c(6, 10, 11, 0, 1, ACC);
      b.c(2, 10, 11, 1, 10, METAL_L);
      b.c(4, 5, 8, 3, 10, PRI);
      for (let z = 4; z < 10; z += 2) b.sym(1, 6, z, PRI_D);
      b.part(null);
      b.c(4, 5, 11, 10, 14, SEC).sides(1, 7, 9, 11, 13, ACC);
      b.c(2, 11, 12, 12, 14, DARK);
      b.c(2, 3, 4, 11, 14, METAL_D).c(2, 4, 5, 11, 12, METAL_D).c(2, 4, 5, 12, 13, ACC);
      b.slant(2, 7, 0, 14, 3, 0.45, PRI);
      b.c(2, 0, 1, 16, 19, ACC);
      b.bevel();
      return {
        grip: [3, 3, 16],
        muzzle: [3, 9, 0],
        fore: [3, 5, 7],
        sight: [3, 11.2, 12],
        rig: rig('rifle', 'break'),
        parts: { break: { pivot: [3, 6.5, 10], axis: 'x' } },
      };
    },
  },

  // Carbine ---------------------------------------------------------------------------------
  pulse_carbine: {
    size: [6, 17, 32],
    build(b, f) {
      b.c(2, 9, 11, 0, 2, GLOW).c(4, 8, 12, 1, 4, SEC).cc(2, 9, 11, 1, 2);
      b.c(2, 9, 11, 1, 2, GLOW_HI);
      b.c(4, 8, 12, 4, 16, PRI);
      for (let z = 5; z < 16; z += 2) b.sym(1, 10, z, GLOW);
      b.c(2, 12, 13, 5, 15, SEC).c(2, 13, 14, 7, 12, SEC);
      b.c(2, 7, 8, 5, 15, SEC);
      b.c(4, 6, 13, 15, 26, PRI);
      b.sides(1, 7, 10, 16, 25, SEC);
      b.sides(1, 11, 12, 16, 25, GLOW);
      b.c(2, 13, 14, 17, 23, SEC); // a narrow top rail, not a wide cover
      // Factory sights: thin irons in the fine layer (a lit front dot), not a box on the receiver.
      ironSights(f, 15.5, 14, 8, 14, 20, GLOW);
      b.part('mag').c(2, 1, 6, 16, 19, SEC).c(2, 2, 5, 17, 18, GLOW).c(2, 1, 2, 16, 19, DARK).part(null);
      triggerGuard(b, 6, 19, 22);
      pistolGrip(b, 6, 1, 22, 3, SEC, 0.4);
      for (let z = 26; z < 32; z++) {
        const bot = 7 + Math.floor((z - 26) * 0.2);
        b.c(4, bot, 13, z, z + 1, PRI);
      }
      b.carve(1, 9, 27, 5, 11, 30).c(4, 7, 13, 31, 32, SEC);
      b.bevel();
      f.bevel();
      return {
        grip: [3, 3.5, 23.5],
        muzzle: [3, 10, 0],
        fore: [3, 7, 10],
        mount: { y: 13 },
        sight: [3, 15.5, 20],
        rig: rig('rifle', 'cell'),
        parts: { mag: { pivot: [3, 6, 17.5], dir: [0, -1, 0] } },
      };
    },
  },

  // Marksman --------------------------------------------------------------------------------
  ranger_br: {
    size: [6, 17, 38],
    build(b) {
      b.c(2, 10, 12, 0, 3, DARK).cc(2, 11, 12, 1, 2);
      b.c(4, 12, 13, 1, 2, DARK).gear(true).c(2, 13, 15, 1, 2, DARK).gear(false); // front post: sight gear
      b.c(2, 10, 12, 3, 24, METAL);
      b.c(2, 8, 10, 5, 12, METAL_D).c(2, 8, 11, 11, 13, METAL_D);
      b.c(4, 7, 12, 12, 24, PRI).c(2, 12, 13, 13, 23, PRI);
      b.c(2, 6, 7, 13, 23, PRI);
      b.c(2, 5, 6, 14, 15, ACC);
      b.c(4, 9, 14, 22, 31, SEC);
      b.box(5, 11, 19, 6, 12, 24, METAL_D);
      b.box(4, 11, 25, 5, 13, 28, DARK);
      b.gear(true).c(4, 14, 15, 27, 29, METAL_D).box(1, 15, 27, 2, 16, 29, DARK).box(4, 15, 27, 5, 16, 29, DARK).gear(false); // rear sight
      b.c(4, 6, 9, 22, 31, PRI);
      magazine(b, 6, 0, 22, 4, 0, DARK);
      triggerGuard(b, 6, 26, 29);
      b.recolor(2, 5, 28, ACC).recolor(3, 5, 28, ACC);
      b.slant(2, 7, 1, 29, 3, 0.45, PRI);
      for (let z = 31; z < 38; z++) {
        const top = z < 35 ? 13 : 12;
        const bot = 7 - Math.floor((z - 31) * 0.55);
        b.c(4, bot, top, z, z + 1, z === 37 ? METAL_D : PRI);
      }
      b.c(2, 3, 4, 34, 35, ACC);
      b.bevel();
      return {
        grip: [3, 3.5, 30.5],
        muzzle: [3, 11, 0],
        fore: [3, 6, 17],
        sight: [3, 15, 28],
        mount: { y: 13 },
        rig: rig('rifle', 'mag'),
        parts: { mag: { pivot: [3, 6, 24], dir: [0, -1, 0] } },
      };
    },
  },

  hunter_dmr: {
    size: [6, 21, 40],
    build(b) {
      b.c(4, 9, 13, 0, 3, SEC).symBox(1, 10, 1, 2, 12, 2, 0).c(2, 10, 12, 0, 1, RUBBER);
      b.c(2, 10, 12, 3, 16, METAL).c(2, 11, 12, 4, 14, METAL_D);
      b.c(4, 8, 13, 10, 23, PRI);
      for (let z = 12; z < 21; z += 3) b.symBox(1, 10, z, 2, 11, z + 2, DARK);
      b.rail(13, 10, 31);
      b.symBox(1, 7, 11, 2, 8, 20, METAL_D).c(4, 7, 8, 10, 12, DARK_L).symBox(1, 7, 19, 2, 8, 20, ACC);
      b.c(4, 7, 13, 22, 31, SEC);
      b.box(4, 10, 24, 5, 12, 28, DARK).box(5, 10, 27, 6, 11, 29, METAL_D);
      b.scope(17, 17, 33, 1.6, 14);
      magazine(b, 7, 1, 23, 3, 0, DARK, ACC);
      triggerGuard(b, 7, 26, 29);
      pistolGrip(b, 7, 1, 28, 3, DARK, 0.45);
      b.c(4, 7, 13, 31, 40, PRI).carve(1, 9, 33, 5, 11, 37);
      b.c(2, 13, 14, 32, 38, SEC).c(4, 6, 14, 39, 40, DARK_L);
      b.sides(1, 12, 13, 31, 39, ACC);
      b.bevel();
      return {
        stock: { y0: 14, z0: 17, z1: 33 },
        grip: [3, 3.5, 29.5],
        muzzle: [3, 11, 0],
        fore: [3, 7, 16],
        sight: [3, 17, 31],
        rig: rig('rifle', 'mag'),
        parts: { mag: { pivot: [3, 7, 24.5], dir: [0, -1, 0] } },
      };
    },
  },

  // LMG -------------------------------------------------------------------------------------
  thumper_lmg: {
    size: [8, 19, 40],
    build(b) {
      b.c(2, 11, 13, 0, 3, DARK).cc(2, 12, 13, 1, 2);
      b.c(2, 13, 16, 2, 3, DARK);
      b.c(2, 11, 13, 3, 24, METAL);
      b.c(4, 10, 14, 3, 4, METAL_D);
      // Carry handle: sight gear (a fitted sight replaces it; left standing it fills the window of a scope).
      b.gear(true).c(2, 13, 15, 11, 12, DARK_L).c(2, 13, 15, 18, 19, DARK_L).c(2, 15, 17, 11, 19, DARK_L).c(2, 17, 18, 12, 18, ACC).gear(false);
      b.c(2, 9, 11, 5, 20, METAL_D);
      b.symBox(2, 8, 6, 3, 9, 16, METAL_D).symBox(2, 7, 6, 3, 8, 7, ACC);
      b.c(6, 8, 14, 16, 24, PRI);
      for (let z = 17; z < 23; z += 2) b.symBox(1, 10, z, 2, 12, z + 1, DARK);
      b.c(6, 7, 15, 23, 33, PRI).c(6, 15, 16, 24, 31, SEC).gear(true).c(2, 16, 17, 29, 31, ACC).gear(false); // the accent strip is the rear sight's base: sight gear
      b.gear(true).c(4, 16, 17, 30, 32, METAL_D).box(2, 17, 30, 3, 18, 32, DARK).box(5, 17, 30, 6, 18, 32, DARK).gear(false); // rear sight
      b.box(7, 11, 25, 8, 12, 28, METAL_D);
      b.part('mag').c(6, 1, 7, 24, 31, SEC).c(6, 1, 2, 24, 31, SEC_D).box(1, 3, 26, 2, 5, 29, ACC).part(null);
      for (let z = 25; z < 30; z++) b.box(0, 7, z, 1, 9, z + 1, z % 2 ? BRASS : METAL_D);
      triggerGuard(b, 7, 29, 32);
      pistolGrip(b, 7, 1, 31, 3, DARK, 0.45);
      b.c(4, 8, 15, 33, 40, SEC).carve(2, 10, 35, 6, 13, 38);
      b.c(4, 7, 15, 39, 40, DARK);
      b.bevel();
      return {
        grip: [4, 3.5, 32.5],
        muzzle: [4, 12, 0],
        fore: [4, 8, 20],
        sight: [4, 16, 31],
        // The cover ridge on top of the receiver (row 15, z 24-30) runs up to the sight: the sight's rail stands on it.
        mount: { y: 15 },
        rig: rig('heavy', 'belt'),
        parts: { mag: { pivot: [4, 7, 27.5], dir: [-0.4, -1, 0] } },
      };
    },
  },

  // Nail gun --------------------------------------------------------------------------------
  nail_driver: {
    size: [6, 17, 27],
    build(b) {
      b.c(2, 8, 10, 0, 5, METAL).c(2, 8, 10, 0, 1, DARK_L);
      b.c(4, 7, 11, 1, 2, RUBBER);
      b.c(4, 6, 15, 4, 11, PRI).c(4, 14, 15, 4, 11, SEC).gear(true).c(2, 15, 16, 5, 10, SEC).gear(false); // the top strip of the feed box is sight gear (a fitted sight replaces it)
      for (let y = 7; y < 14; y += 2) b.sides(1, y, y + 1, 4, 11, SEC);
      b.c(4, 7, 13, 10, 21, PRI);
      b.c(2, 13, 14, 12, 20, SEC_D);
      b.gear(true).box(1, 14, 18, 2, 16, 20, DARK).box(4, 14, 18, 5, 16, 20, DARK).c(4, 13, 14, 18, 20, DARK).gear(false); // rear sight: sight gear
      b.box(4, 9, 13, 5, 11, 17, DARK);
      b.part('mag').tubeX(3.5, 9, 1, 5, 3.3, SEC).tubeX(3.5, 9, 0, 6, 1.2, ACC);
      for (let a = 0; a < 8; a++) {
        const y = Math.floor(3.5 + Math.sin(a * 0.785) * 2.2);
        const z = Math.floor(9 + Math.cos(a * 0.785) * 2.2);
        b.sides(1, y, y + 1, z, z + 1, ACC);
      }
      b.part(null);
      triggerGuard(b, 7, 14, 17);
      pistolGrip(b, 7, 0, 17, 3, SEC, 0.4);
      b.c(2, 9, 11, 21, 23, BRASS).c(2, 9, 11, 23, 27, DARK).c(2, 8, 9, 25, 27, DARK);
      b.bevel();
      return {
        grip: [3, 3.5, 18.5],
        muzzle: [3, 9, 0],
        fore: [3, 7, 13],
        sight: [3, 16, 19],
        // The feed box in front (top row 14) is taller than the receiver behind it (row 13): the sight rides one row higher so the box stays out of its window.
        mount: { y: 14 },
        rig: rig('rifle', 'mag'),
        parts: { mag: { pivot: [3, 6.8, 9], dir: [0, -1, 0] } },
      };
    },
  },

  // Minigun ---------------------------------------------------------------------------------
  voxel_shredder: {
    size: [9, 17, 38],
    build(b) {
      const cx = 4.5;
      const cy = 9.5;
      // Six barrels in a hexagon around a hub, held by two clamps; the whole cluster spins.
      b.part('spin');
      b.tube(cx, cy, 0, 20, 0.6, METAL_D);
      const barrels: [number, number][] = [[6, 9], [5, 11], [3, 11], [2, 9], [3, 7], [5, 7]];
      for (const [x, y] of barrels) b.box(x, y, 0, x + 1, y + 1, 20, METAL).set(x, y, 0, RUBBER);
      for (const z of [2, 11]) b.tube(cx, cy, z, z + 2, 3.3, DARK, 0).tube(cx, cy, z, z + 2, 3.3, DARK_L, 2.6);
      for (const [x, y] of barrels) b.set(x, y, 2, METAL_D).set(x, y, 11, METAL_D);
      b.part(null);
      b.tube(cx, cy, 19, 29, 3.9, PRI);
      for (const z of [20, 23, 26]) b.tube(cx, cy, z, z + 1, 4.2, GLOW, 3.8);
      b.tube(cx, cy, 28, 31, 3.1, SEC).tube(cx, cy, 30, 31, 1.6, GLOW);
      b.box(0, 5, 22, 1, 12, 24, DARK).box(0, 12, 21, 2, 13, 25, DARK_L).box(0, 4, 21, 2, 5, 25, DARK_L);
      b.box(3, 14, 20, 6, 15, 22, SEC).box(3, 14, 26, 6, 15, 28, SEC).box(3, 15, 20, 6, 16, 28, SEC).box(4, 16, 21, 5, 17, 27, SEC_L);
      b.part('mag').box(2, 0, 20, 7, 5, 28, SEC).box(2, 2, 20, 7, 3, 28, ACC).part(null);
      b.box(8, 6, 21, 9, 10, 27, SEC_D);
      b.box(3, 1, 29, 6, 7, 32, DARK).box(3, 0, 30, 6, 1, 32, DARK_L);
      b.box(1, 6, 30, 8, 8, 32, DARK);
      b.bevel();
      return {
        grip: [4.5, 3.5, 30.5],
        muzzle: [4.5, cy, 0],
        fore: [0.5, 8.5, 23],
        sight: [4.5, 17, 25],
        rig: rig('low', 'belt', 'spin'),
        parts: { spin: { pivot: [cx, cy, 10], axis: 'z' }, mag: { pivot: [4.5, 5, 24], dir: [0, -1, 0] } },
      };
    },
  },

  // Pistols ---------------------------------------------------------------------------------
  peashooter: {
    size: [4, 10, 11],
    build(b, f) {
      // Slide (animated): flat top, ejection port, muzzle bush. Its sights are fine sight gear on top.
      b.part('slide').c(2, 6, 9, 0, 11, PRI);
      b.box(2, 8, 3, 3, 9, 6, DARK);
      b.c(2, 7, 8, 0, 1, METAL_D).set(1, 7, 0, RUBBER);
      b.part(null);
      b.c(2, 5, 6, 0, 10, SEC).c(2, 4, 5, 1, 4, SEC).c(2, 5, 6, 0, 3, METAL_D);
      triggerGuard(b, 5, 3, 6);
      b.slant(2, 5, 1, 6, 4, 0.3, SEC);
      b.sides(1, 3, 4, 7, 8, ACC);
      b.part('mag').c(2, 0, 1, 7, 10, DARK_L).c(2, 1, 2, 7, 10, ACC).part(null);
      // --- Fine hardware ---
      f.part('slide');
      // The "pea": a gold bead front sight. Rear: two posts with gold tips. Between them a ribbed top.
      f.gear(true);
      f.box(3, 18, 2, 5, 20, 4, BRASS);
      f.box(2, 18, 17, 3, 21, 20, DARK).box(5, 18, 17, 6, 21, 20, DARK).box(2, 20, 17, 3, 21, 20, BRASS).box(5, 20, 17, 6, 21, 20, BRASS);
      for (let hz = 6; hz < 16; hz += 2) f.c(2, 18, 19, hz, hz + 1, METAL_D);
      f.gear(false);
      // Rear serrations and an ejection-port frame on both sides.
      for (const hz of [14, 16, 18]) f.box(1, 14, hz, 2, 17, hz + 1, METAL_D).box(6, 14, hz, 7, 17, hz + 1, METAL_D);
      sideFrame(f, 6, 16, 18, 5, 12, METAL_D);
      f.part(null);
      triggerBlade(f, 4, 4);
      for (let y = 1; y < 5; y++)
        for (let hy = y * D; hy < y * D + D; hy++)
          for (let hz = 6 * D + 1; hz < 10 * D - 1; hz++) if ((hy + hz) % 2 === 0) f.set(1, hy, hz, DARK_L).set(6, hy, hz, DARK_L);
      b.bevel();
      f.bevel();
      return {
        mount: { y: 8 },
        grip: [2, 3, 8],
        muzzle: [2, 7.5, 0],
        fore: null,
        sight: [2, 10, 9],
        rig: rig('pistol', 'mag', 'slide'),
        parts: { slide: { pivot: [2, 7.5, 5.5], dir: [0, 0, 1] }, mag: { pivot: [2, 2, 8.5], dir: [0, -1, 0.1] } },
      };
    },
  },

  machine_pistol: {
    size: [4, 11, 14],
    build(b) {
      b.c(2, 7, 10, 0, 3, DARK_L).cc(2, 9, 10, 1, 2).c(2, 7, 9, 0, 1, RUBBER);
      b.part('slide').c(2, 7, 10, 3, 14, PRI);
      b.sides(1, 8, 9, 4, 8, SEC);
      for (const z of [10, 12]) b.sides(1, 8, 10, z, z + 1, PRI_D);
      b.gear(true).c(2, 10, 11, 4, 5, ACC).c(2, 10, 11, 11, 13, DARK).gear(false); // front and rear sights: sight gear
      b.part(null);
      b.c(2, 6, 7, 3, 13, PRI_D).c(2, 5, 6, 3, 7, PRI_D);
      b.box(0, 6, 9, 1, 7, 11, SEC);
      triggerGuard(b, 6, 6, 9);
      b.slant(2, 6, 2, 9, 4, 0.3, PRI);
      b.sides(1, 3, 4, 9, 12, RUBBER);
      b.part('mag').c(2, 0, 2, 10, 13, SEC).c(2, 0, 1, 10, 13, ACC).part(null);
      b.bevel();
      return {
        grip: [2, 4, 11],
        muzzle: [2, 8.5, 0],
        fore: null,
        sight: [2, 11, 12],
        rig: rig('pistol', 'mag', 'slide'),
        parts: { slide: { pivot: [2, 8.5, 8], dir: [0, 0, 1] }, mag: { pivot: [2, 2, 11.5], dir: [0, -1, 0.1] } },
      };
    },
  },

  hand_cannon: {
    size: [6, 12, 17],
    build(b) {
      b.c(2, 6, 8, 0, 9, PRI).c(2, 5, 6, 0, 8, PRI_D).c(2, 8, 9, 0, 9, PRI_D);
      b.gear(true).c(2, 9, 10, 0, 2, ACC).gear(false); // front blade: sight gear
      b.c(2, 6, 8, 0, 1, DARK).c(2, 7, 8, 0, 1, RUBBER);
      b.part('cyl').tube(3, 7, 9, 13, 2.2, PRI_D);
      for (let a = 0; a < 6; a++) b.recolor(Math.floor(3 + Math.cos(a * 1.047 + 0.52) * 1.6), Math.floor(7 + Math.sin(a * 1.047 + 0.52) * 1.6), 9, DARK);
      b.sides(1, 6, 8, 10, 12, LIGHT);
      b.part(null);
      b.c(4, 9, 10, 8, 10, PRI).gear(true).c(4, 9, 10, 10, 14, PRI).cc(2, 9, 10, 12, 13).gear(false); // the part of the top strap under a fitted sight is sight gear
      b.c(4, 4, 6, 8, 9, PRI).c(4, 4, 10, 13, 15, PRI);
      b.c(2, 9, 10, 14, 17, DARK).c(2, 8, 9, 15, 17, DARK_L);
      triggerGuard(b, 4, 10, 13);
      b.slant(2, 5, 0, 13, 3, 0.55, SEC);
      b.sides(2, 2, 4, 14, 16, ACC);
      b.c(2, 0, 1, 15, 18, METAL_D);
      b.bevel();
      return {
        grip: [3, 2.5, 14.5],
        muzzle: [3, 7, 0],
        fore: null,
        sight: [3, 10.2, 12],
        // The hammer behind the sight (row 9) is close to the eye and reaches the bottom of a window sitting level with it: ride one row higher.
        mount: { y: 10 },
        rig: rig('pistol', 'revolver', 'cyl'),
        parts: { cyl: { pivot: [3, 7, 11], axis: 'z', dir: [-1, 0, 0] } },
      };
    },
  },

  flare_gun: {
    size: [6, 12, 13],
    build(b) {
      b.part('break');
      b.tube(3, 8, 0, 9, 2.2, PRI).tube(3, 8, 0, 1, 2.6, ACC).tube(3, 8, 0, 1, 1.2, GLOW).tube(3, 8, 1, 2, 1.2, GLOW_HI);
      b.tube(3, 8, 7, 9, 2.6, ACC);
      b.part(null);
      b.c(4, 4, 6, 7, 10, SEC);
      b.c(4, 5, 10, 9, 12, PRI);
      b.c(2, 10, 11, 10, 13, SEC);
      b.c(2, 10, 11, 1, 2, SEC);
      triggerGuard(b, 5, 9, 12);
      b.slant(2, 5, 0, 10, 3, 0.4, SEC);
      b.sides(2, 1, 2, 10, 13, ACC).sides(2, 3, 4, 10, 13, ACC);
      b.bevel();
      return {
        grip: [3, 2.5, 11.5],
        muzzle: [3, 8, 0],
        fore: null,
        sight: [3, 11.2, 10],
        rig: rig('pistol', 'break'),
        parts: { break: { pivot: [3, 5.5, 8.5], axis: 'x' } },
      };
    },
  },

  photon_pistol: {
    size: [4, 11, 13],
    build(b) {
      b.c(2, 6, 9, 0, 2, SEC).c(2, 7, 8, 0, 1, GLOW_HI);
      b.c(2, 6, 9, 2, 8, PRI);
      for (const z of [3, 5]) b.c(4, 5, 10, z, z + 1, SEC).c(4, 7, 8, z, z + 1, GLOW);
      b.c(2, 5, 10, 7, 13, PRI).c(2, 9, 10, 8, 12, SEC).c(2, 10, 11, 10, 12, GLOW);
      b.sides(1, 7, 8, 7, 12, GLOW);
      triggerGuard(b, 5, 6, 9);
      b.slant(2, 5, 0, 9, 3, 0.35, SEC);
      b.part('mag').c(2, 0, 2, 10, 12, DARK).c(2, 1, 2, 10, 12, GLOW).part(null);
      b.bevel();
      return {
        grip: [2, 2.5, 10.5],
        muzzle: [2, 7.5, 0],
        fore: null,
        sight: [2, 10.5, 11],
        rig: rig('pistol', 'cell'),
        parts: { mag: { pivot: [2, 2, 11], dir: [0, -1, 0.1] } },
      };
    },
  },

  // Melee (the business end is at -Z) --------------------------------------------------------
  builders_hammer: {
    size: [10, 13, 32],
    build(b) {
      b.box(2, 1, 0, 8, 10, 6, PRI);
      b.box(2, 0, 1, 8, 1, 5, METAL_L).box(1, 1, 0, 9, 3, 6, METAL);
      b.box(2, 10, 1, 8, 11, 5, PRI).box(3, 11, 2, 7, 12, 5, PRI).box(4, 11, 2, 6, 13, 5, 0).box(3, 12, 4, 4, 13, 6, PRI).box(6, 12, 4, 7, 13, 6, PRI);
      b.box(2, 5, 0, 8, 7, 6, ACC);
      b.box(4, 5, 6, 6, 7, 32, SEC);
      b.box(3, 4, 23, 7, 8, 31, ACC);
      for (let z = 24; z < 31; z += 2) b.box(3, 4, z, 7, 8, z + 1, ACC_D);
      b.box(3, 4, 31, 7, 8, 32, DARK);
      b.bevel();
      return {
        grip: [5, 6, 27.5],
        muzzle: [5, 1, 3],
        fore: null,
        sight: [5, 6, 3],
        rig: rig('melee', 'none', 'none', 'chop'),
      };
    },
  },

  pickaxe: {
    size: [6, 24, 32],
    build(b) {
      for (let y = 0; y < 24; y++) {
        const dy = Math.abs(y - 11.5);
        const back = Math.floor((dy * dy) / 38);
        const thick = dy > 9 ? 1 : dy > 5 ? 2 : 3;
        b.c(2, y, y + 1, back, back + thick, dy > 9.5 ? METAL_L : PRI);
      }
      b.c(4, 9, 15, 1, 6, METAL_D);
      b.c(2, 11, 13, 5, 32, SEC);
      b.c(4, 10, 14, 23, 31, ACC);
      for (let z = 24; z < 31; z += 2) b.c(4, 10, 14, z, z + 1, ACC_D);
      b.c(4, 10, 14, 31, 32, DARK);
      b.bevel();
      return {
        grip: [3, 12, 27.5],
        muzzle: [3, 0, 3],
        fore: null,
        sight: [3, 12, 3],
        rig: rig('melee', 'none', 'none', 'chop'),
      };
    },
  },

  frying_pan: {
    size: [4, 16, 31],
    build(b) {
      for (let y = 0; y < 16; y++)
        for (let z = 0; z < 16; z++) {
          const d = Math.hypot(y + 0.5 - 8, z + 0.5 - 8);
          if (d > 7.9) continue;
          b.box(1, y, z, 3, y + 1, z + 1, d > 6.6 ? ACC : PRI);
          if (d > 6.6) b.box(0, y, z, 1, y + 1, z + 1, ACC_L);
        }
      b.box(3, 4, 4, 4, 12, 12, PRI_D);
      b.box(1, 7, 15, 3, 9, 20, ACC).box(1, 7, 20, 3, 9, 31, SEC);
      b.box(1, 7, 28, 3, 9, 29, 0);
      b.box(0, 7, 16, 4, 9, 17, METAL_L);
      b.bevel();
      return {
        grip: [2, 8, 25],
        muzzle: [2, 8, 8],
        fore: null,
        sight: [2, 8, 8],
        rig: rig('melee', 'none', 'none', 'bash'),
      };
    },
  },

  zombie_claws: {
    size: [8, 10, 16],
    build(b) {
      b.box(1, 2, 8, 7, 8, 16, PRI).box(2, 8, 9, 6, 9, 15, PRI_D);
      for (const x of [1, 3, 5]) {
        for (let z = 0; z < 9; z++) {
          const y = 5 - Math.floor((8 - z) / 3);
          b.box(x, y, z, x + 2, y + 1, z + 1, z < 2 ? GLOW : LIGHT);
        }
      }
      b.box(1, 4, 11, 7, 5, 13, GLOW);
      b.bevel();
      return {
        grip: [4, 5, 12],
        muzzle: [4, 3, 0],
        fore: null,
        sight: [4, 9, 12],
        rig: rig('pistol', 'none', 'none', 'swipe', 0.72),
      };
    },
  },

  plasma_blade: {
    size: [6, 9, 40],
    build(b) {
      b.c(2, 4, 5, 0, 1, GLOW).c(2, 3, 6, 1, 27, GLOW).c(2, 4, 5, 2, 26, GLOW_HI);
      b.c(6, 2, 7, 27, 29, ACC).c(2, 2, 7, 26, 28, ACC).c(4, 1, 8, 27, 28, ACC_D);
      b.sym(0, 4, 27, GLOW);
      b.c(2, 3, 6, 29, 40, PRI);
      for (let z = 30; z < 38; z += 2) b.c(2, 3, 6, z, z + 1, DARK_L);
      b.c(4, 3, 6, 38, 40, ACC);
      b.bevel();
      return {
        grip: [3, 4.5, 34],
        muzzle: [3, 4.5, 3],
        fore: null,
        sight: [3, 4.5, 3],
        rig: rig('melee', 'none', 'none', 'slash'),
      };
    },
  },

  buzz_blade: {
    size: [6, 22, 30],
    build(b) {
      const cy = 11;
      const cz = 10;
      b.part('spin');
      for (let y = 0; y < 22; y++)
        for (let z = 0; z < 21; z++) {
          const d = Math.hypot(y + 0.5 - cy, z + 0.5 - cz);
          if (d > 10) continue;
          let v = METAL;
          if (d > 9) {
            const a = Math.atan2(y + 0.5 - cy, z + 0.5 - cz);
            if (Math.floor((a / (Math.PI * 2)) * 24 + 24) % 2) continue;
            v = METAL_L;
          } else if (d < 2) v = ACC;
          else if (d > 4.2 && d < 5.2) v = METAL_D;
          b.c(2, y, y + 1, z, z + 1, v);
        }
      b.part(null);
      for (let y = 0; y < 22; y++)
        for (let z = 0; z < 22; z++) {
          const d = Math.hypot(y + 0.5 - cy, z + 0.5 - cz);
          if (d < 10.2 || d > 11.6 || y < cy - 2 || z < cz - 7) continue;
          b.box(1, y, z, 5, y + 1, z + 1, PRI);
        }
      b.box(1, 12, 13, 2, 21, 20, PRI).box(4, 12, 13, 5, 21, 20, PRI);
      b.box(1, 7, 17, 5, 15, 25, SEC).box(1, 9, 18, 5, 10, 24, DARK_L).box(1, 12, 18, 5, 13, 24, DARK_L);
      b.c(4, 10, 12, 8, 12, ACC);
      b.c(2, 9, 12, 25, 30, DARK).c(2, 9, 12, 29, 30, ACC);
      b.bevel();
      return {
        grip: [3, 10.5, 27.5],
        muzzle: [3, 11, 1],
        fore: null,
        sight: [3, 16, 21],
        rig: rig('pistol', 'none', 'spin', 'thrust', 0.62),
        parts: { spin: { pivot: [3, 11, 10], axis: 'x' } },
      };
    },
  },

  katana: {
    size: [6, 9, 43],
    build(b) {
      for (let z = 0; z < 30; z++) {
        const lift = Math.floor(((30 - z) * (30 - z)) / 300);
        const yb = 3 + lift;
        const h = z < 2 ? 1 : z < 4 ? 2 : 3;
        b.c(2, yb, yb + h, z, z + 1, PRI);
        if (h === 3) b.c(2, yb + 2, yb + 3, z, z + 1, METAL_L).c(2, yb + 1, yb + 2, z, z + 1, LIGHT);
      }
      b.c(2, 3, 6, 30, 31, BRASS);
      b.c(6, 1, 8, 31, 32, DARK).c(4, 0, 9, 31, 32, DARK).sides(0, 3, 6, 31, 32, ACC);
      b.c(2, 3, 6, 32, 42, SEC);
      for (let z = 32; z < 42; z++) for (let y = 3; y < 6; y++) if ((y + z) % 2 === 0) b.sides(2, y, y + 1, z, z + 1, ACC);
      b.c(2, 3, 6, 42, 43, METAL_D);
      return {
        grip: [3, 4.5, 37],
        muzzle: [3, 4, 3],
        fore: null,
        sight: [3, 4, 3],
        rig: rig('melee', 'none', 'none', 'slash'),
      };
    },
  },

  // Heavy -----------------------------------------------------------------------------------
  rocket_launcher: {
    size: [10, 18, 40],
    build(b) {
      const cy = 11;
      b.tube(5, cy, 4, 36, 3.6, PRI).tube(5, cy, 4, 6, 3.6, DARK, 2.3);
      b.tube(5, cy, 3, 6, 4.3, DARK, 3.0);
      b.tube(5, cy, 14, 25, 4.2, SEC);
      for (let z = 15; z < 25; z += 2) b.tube(5, cy, z, z + 1, 4.3, SEC_D, 3.9);
      b.tube(5, cy, 29, 31, 4.0, ACC);
      b.tube(5, cy, 35, 40, 4.5, DARK, 3.2).tube(5, cy, 36, 40, 3.2, RUBBER, 2.4);
      b.part('mag');
      b.tube(5, cy, 3, 7, 2.6, ACC).tube(5, cy, 1, 3, 2.0, ACC).tube(5, cy, 0, 1, 1.1, LIGHT);
      b.tube(5, cy, 5, 7, 1.0, ACC_D);
      b.part(null);
      b.box(4, 2, 15, 6, 8, 18, SEC).box(4, 1, 15, 6, 2, 18, DARK);
      b.box(4, 3, 22, 6, 8, 25, DARK).box(4, 1, 23, 6, 3, 26, DARK).box(4, 0, 24, 6, 1, 26, DARK_L);
      b.box(4, 5, 20, 6, 6, 23, METAL_D);
      b.box(0, 12, 17, 2, 15, 23, DARK).box(0, 12, 17, 1, 15, 18, LENS).box(0, 13, 22, 1, 14, 23, LENS_HI);
      b.box(2, 13, 19, 3, 14, 21, METAL_D);
      b.c(2, 15, 17, 7, 8, DARK).c(4, 15, 16, 25, 27, METAL_D).box(3, 16, 25, 4, 17, 27, DARK).box(6, 16, 25, 7, 17, 27, DARK);
      b.bevel();
      return {
        grip: [5, 4, 24.5],
        muzzle: [5, cy, 1],
        fore: [5, 4.5, 16.5],
        sight: [5, 17, 26],
        rig: rig('shoulder', 'launcher'),
        parts: { mag: { pivot: [5, cy, 4], dir: [0, 0, -1] } },
      };
    },
  },

  mining_laser: {
    size: [10, 17, 32],
    build(b) {
      const cy = 9;
      b.tube(5, cy, 0, 3, 3.6, SEC).tube(5, cy, 0, 1, 2.2, GLOW).tube(5, cy, 0, 1, 1.0, GLOW_HI);
      b.tube(5, cy, 0, 1, 3.6, ACC, 2.8);
      b.tube(5, cy, 3, 10, 2.2, METAL);
      for (const z of [4, 7]) b.tube(5, cy, z, z + 1, 3.3, DARK).tube(5, cy, z, z + 1, 3.3, GLOW, 2.9);
      b.box(1, 4, 9, 9, 14, 23, PRI);
      for (let y = 4; y < 14; y++) for (let z = 9; z < 23; z++) if (((y + z) >> 1) % 2 === 0 && y < 8) b.recolor(1, y, z, SEC).recolor(8, y, z, SEC);
      for (let z = 11; z < 21; z += 2) b.box(2, 14, z, 8, 16, z + 1, METAL_D);
      b.box(3, 16, 12, 7, 17, 18, DARK_L);
      b.box(0, 9, 12, 1, 11, 20, GLOW);
      b.part('mag').box(2, 0, 13, 8, 4, 19, SEC).box(3, 1, 13, 7, 3, 14, GLOW).part(null);
      b.box(4, 0, 20, 6, 4, 23, DARK).box(4, 0, 21, 6, 1, 24, DARK_L);
      b.box(3, 5, 23, 7, 12, 32, SEC).box(3, 4, 31, 7, 13, 32, DARK).carve(3, 7, 25, 7, 10, 30);
      b.bevel();
      return {
        grip: [5, 2, 21.5],
        muzzle: [5, cy, 0],
        fore: [5, 4, 11.5],
        sight: [5, 17, 16],
        rig: rig('heavy', 'cell'),
        parts: { mag: { pivot: [5, 4, 16], dir: [0, -1, 0] } },
      };
    },
  },

  bouncer: {
    size: [10, 17, 31],
    build(b) {
      b.tube(5, 11, 0, 10, 2.2, PRI).tube(5, 11, 0, 2, 1.3, DARK).tube(5, 11, 0, 1, 2.6, DARK, 1.3);
      b.box(4, 13, 1, 6, 14, 12, DARK);
      b.part('cyl').tube(5, 9, 10, 17, 4.2, SEC);
      for (let a = 0; a < 6; a++) {
        const px = 5 + Math.cos(a * 1.047) * 2.6;
        const py = 9 + Math.sin(a * 1.047) * 2.6;
        b.tube(px, py, 10, 11, 0.9, ACC);
      }
      for (let z = 12; z < 16; z++) if (z % 2) b.tube(5, 9, z, z + 1, 4.3, SEC_D, 3.8);
      b.part(null);
      b.box(4, 14, 8, 6, 15, 21, PRI).box(3, 13, 16, 7, 14, 21, PRI).box(3, 4, 17, 7, 14, 20, PRI);
      b.box(3, 15, 14, 7, 17, 16, ACC).box(4, 16, 14, 6, 17, 16, 0);
      b.box(4, 4, 3, 6, 8, 6, SEC).box(4, 3, 3, 6, 4, 6, ACC);
      triggerGuard(b, 4, 18, 21);
      pistolGrip(b, 4, 0, 20, 3, SEC, 0.4);
      b.box(4, 11, 20, 6, 13, 30, SEC).box(4, 5, 20, 6, 7, 30, SEC).box(3, 5, 29, 7, 13, 31, DARK);
      b.bevel();
      return {
        grip: [5, 2, 21],
        muzzle: [5, 11, 0],
        fore: [5, 4, 4.5],
        sight: [5, 16.2, 15],
        rig: rig('rifle', 'revolver', 'cyl'),
        parts: { cyl: { pivot: [5, 9, 13.5], axis: 'z', dir: [-1, 0, 0] } },
      };
    },
  },

  mortar: {
    size: [10, 17, 34],
    build(b) {
      const cy = 10;
      // Fat launch tube with a flared muzzle; the next shell's nose shows in the bore.
      b.tube(5, cy, 3, 26, 3.7, PRI).tube(5, cy, 3, 6, 2.6, DARK);
      b.tube(5, cy, 0, 4, 4.5, PRI_D, 2.9).tube(5, cy, 0, 1, 4.5, ACC, 3.6);
      for (const z of [9, 15, 21]) b.tube(5, cy, z, z + 1, 4.0, ACC);
      b.part('mag').tube(5, cy, 2, 6, 2.5, ACC).tube(5, cy, 1, 2, 1.8, ACC).tube(5, cy, 0, 1, 1.0, BRASS).part(null);
      // Breech block, grips and a short stock.
      b.box(2, 6, 24, 8, 15, 30, SEC).box(2, 13, 25, 8, 14, 29, SEC_D);
      b.box(4, 15, 25, 6, 16, 29, METAL_D);
      b.box(0, 11, 12, 1, 15, 19, DARK).box(0, 12, 12, 1, 14, 13, LENS).box(0, 15, 14, 1, 16, 18, METAL_D).box(1, 12, 14, 2, 14, 17, DARK);
      b.box(4, 1, 13, 6, 7, 16, SEC).box(4, 0, 13, 6, 1, 16, DARK_L);
      triggerGuard(b, 6, 25, 28);
      pistolGrip(b, 6, 0, 27, 3, SEC, 0.4);
      b.box(3, 7, 30, 7, 13, 34, PRI_D).carve(3, 9, 31, 7, 11, 33).box(3, 6, 33, 7, 14, 34, DARK);
      b.c(2, 14, 16, 3, 4, DARK);
      b.bevel();
      return {
        grip: [5, 3, 28.5],
        muzzle: [5, cy, 0],
        fore: [5, 3.5, 14.5],
        sight: [5, 16, 27],
        rig: rig('heavy', 'launcher'),
        parts: { mag: { pivot: [5, cy, 3], dir: [0, 0, -1] } },
      };
    },
  },

  dragons_breath: {
    size: [10, 17, 36],
    build(b) {
      b.tube(5, 11, 0, 4, 1.8, METAL).tube(5, 11, 0, 1, 2.3, DARK).tube(5, 11, 0, 1, 1.0, GLOW_HI);
      b.box(4, 8, 0, 6, 9, 3, METAL_D).box(4, 8, 0, 6, 9, 1, GLOW);
      b.tube(5, 11, 4, 18, 1.2, METAL_D).tube(5, 11, 6, 16, 2.6, PRI);
      for (let z = 7; z < 16; z += 2) b.box(2, 10, z, 3, 12, z + 1, 0).box(7, 10, z, 8, 12, z + 1, 0);
      b.box(3, 8, 15, 7, 14, 28, SEC).box(4, 14, 10, 6, 16, 11, DARK).box(4, 14, 17, 6, 16, 18, DARK).box(4, 15, 10, 6, 16, 18, DARK_L);
      b.part('mag').tube(5, 4.5, 8, 24, 3.2, PRI).tube(5, 4.5, 11, 12, 3.4, ACC).tube(5, 4.5, 20, 21, 3.4, ACC).tube(5, 4.5, 24, 25, 1.5, BRASS).part(null);
      b.box(1, 10, 19, 3, 13, 22, WHITE).box(1, 11, 20, 2, 12, 21, RED).box(1, 13, 19, 3, 14, 22, DARK);
      b.box(7, 7, 18, 9, 9, 25, DARK);
      pistolGrip(b, 8, 1, 25, 3, DARK, 0.4);
      b.box(4, 7, 22, 6, 8, 25, METAL_D);
      b.box(3, 8, 28, 7, 13, 36, SEC).carve(3, 9, 30, 7, 11, 34).box(3, 7, 35, 7, 14, 36, DARK);
      b.bevel();
      return {
        grip: [5, 4, 26.5],
        muzzle: [5, 11, 0],
        fore: [5, 8.2, 10],
        sight: [5, 16.5, 16],
        rig: rig('rifle', 'cell'),
        parts: { mag: { pivot: [5, 7.7, 16], dir: [0, -1, 0] } },
      };
    },
  },

  plasma_cannon: {
    size: [12, 18, 34],
    build(b) {
      const cy = 10;
      b.tube(6, cy, 0, 2, 4.2, SEC, 2.4).tube(6, cy, 0, 2, 2.4, GLOW).tube(6, cy, 0, 1, 1.2, GLOW_HI);
      for (const [px, py] of [[6, cy + 4.5], [1.5, cy - 2], [10.5, cy - 2]] as const) b.tube(px, py, 0, 5, 0.9, ACC);
      b.tube(6, cy, 2, 4, 3.0, METAL_D);
      b.tube(6, cy, 4, 14, 5.2, PRI).tube(6, cy, 5, 13, 3.6, GLOW).tube(6, cy, 6, 12, 1.8, GLOW_HI);
      for (let z = 5; z < 13; z += 2) {
        b.box(0, cy - 1, z, 12, cy + 1, z + 1, GLOW);
        b.tube(6, cy, z, z + 1, 3.5, GLOW);
      }
      b.box(2, 5, 13, 10, 15, 25, SEC).box(2, 11, 14, 10, 12, 24, PRI).box(2, 7, 14, 10, 8, 24, PRI);
      b.box(4, 15, 15, 8, 16, 22, DARK).box(4, 16, 15, 5, 18, 17, DARK).box(7, 16, 15, 8, 18, 17, DARK).box(4, 17, 15, 8, 18, 17, ACC);
      b.part('mag').box(4, 1, 15, 8, 5, 21, PRI).box(4, 2, 15, 8, 4, 21, GLOW).box(4, 1, 15, 8, 2, 21, DARK).part(null);
      triggerGuard(b, 5, 20, 23);
      pistolGrip(b, 5, 0, 22, 3, DARK, 0.4);
      b.box(4, 7, 25, 8, 14, 34, SEC).carve(4, 9, 27, 8, 12, 32).box(4, 6, 33, 8, 15, 34, PRI);
      b.bevel();
      return {
        grip: [6, 2.5, 23.5],
        muzzle: [6, cy, 0],
        fore: [6, 4.8, 9],
        sight: [6, 17, 16],
        rig: rig('heavy', 'cell'),
        parts: { mag: { pivot: [6, 5, 18], dir: [0, -1, 0] } },
      };
    },
  },

  tesla_coil: {
    size: [12, 18, 32],
    build(b) {
      const cy = 10;
      b.ball(6, cy, 2.5, 2.5, GLOW).ball(6, cy, 2.5, 1.4, GLOW_HI);
      b.tube(6, cy, 4, 19, 1.1, METAL_D);
      for (let z = 5; z < 17; z++) b.tube(6, cy, z, z + 1, 3.4, z % 2 ? SEC : SEC_D, 2.0);
      for (const z of [4, 17]) b.tube(6, cy, z, z + 1, 4.3, DARK_L);
      b.box(3, 5, 18, 9, 14, 27, PRI);
      for (let z = 19; z < 26; z += 2) b.box(3, 11, z, 9, 12, z + 1, GLOW);
      b.box(5, 14, 19, 7, 16, 25, DARK_L).box(5, 16, 20, 7, 17, 24, ACC);
      b.part('mag').box(4, 1, 19, 8, 5, 24, ACC_D).box(4, 2, 19, 8, 4, 24, GLOW).part(null);
      triggerGuard(b, 5, 23, 26);
      pistolGrip(b, 5, 0, 25, 3, DARK, 0.4);
      b.box(4, 7, 27, 8, 13, 32, PRI).box(4, 6, 31, 8, 14, 32, DARK);
      b.bevel();
      return {
        grip: [6, 2.5, 26.5],
        muzzle: [6, cy, 0],
        fore: [6, 6.5, 11],
        sight: [6, 17, 22],
        rig: rig('heavy', 'cell'),
        parts: { mag: { pivot: [6, 5, 21.5], dir: [0, -1, 0] } },
      };
    },
  },

  railgun: {
    size: [8, 18, 42],
    build(b) {
      b.box(1, 7, 0, 3, 13, 26, SEC).box(5, 7, 0, 7, 13, 26, SEC);
      b.box(2, 8, 0, 3, 12, 26, METAL_L).box(5, 8, 0, 6, 12, 26, METAL_L);
      b.box(3, 9, 1, 5, 11, 25, GLOW).box(3, 9, 1, 5, 10, 25, GLOW_HI);
      b.box(1, 7, 0, 7, 8, 2, PRI).box(1, 12, 0, 7, 13, 2, PRI);
      for (const z of [7, 14, 21]) b.box(1, 12, z, 7, 14, z + 2, PRI).box(1, 6, z, 7, 7, z + 2, PRI);
      b.box(1, 5, 24, 7, 14, 34, PRI);
      for (let z = 25; z < 33; z += 2) b.box(0, 9, z, 8, 11, z + 1, GLOW);
      b.box(3, 14, 27, 5, 15, 29, ACC).box(3, 14, 31, 5, 15, 33, ACC);
      b.tube(4, 16.5, 25, 34, 1.6, DARK).tube(4, 16.5, 25, 26, 1.6, LENS).set(3, 16, 25, LENS_HI);
      b.part('mag').box(2, 1, 26, 6, 5, 31, SEC).box(2, 2, 26, 6, 4, 31, GLOW).part(null);
      triggerGuard(b, 5, 30, 33);
      pistolGrip(b, 5, 0, 32, 3, DARK, 0.4);
      b.box(2, 7, 34, 6, 14, 42, SEC).carve(2, 9, 35, 6, 12, 40).box(2, 6, 41, 6, 15, 42, PRI);
      b.bevel();
      return {
        grip: [4, 2.5, 33.5],
        muzzle: [4, 10, 0],
        fore: [4, 6, 14],
        sight: [4, 16.5, 30],
        rig: rig('rifle', 'cell'),
        parts: { mag: { pivot: [4, 5, 28.5], dir: [0, -1, 0] } },
      };
    },
  },

  // Snipers ---------------------------------------------------------------------------------
  longshot: {
    size: [6, 20, 44],
    build(b, f) {
      // Muzzle brake with side vents; barrel; fore-end level with the receiver so one rail runs over both.
      b.c(4, 9, 12, 0, 3, DARK).symBox(1, 10, 1, 2, 11, 2, 0).c(2, 10, 11, 0, 1, RUBBER);
      b.c(2, 9, 11, 3, 28, METAL);
      b.c(4, 7, 12, 13, 27, SEC).c(2, 6, 7, 14, 26, SEC);
      b.symBox(1, 6, 15, 2, 7, 23, METAL_D).symBox(1, 5, 22, 2, 6, 24, ACC);
      b.c(4, 7, 12, 26, 34, PRI);
      b.part('bolt').box(4, 10, 31, 6, 11, 32, METAL).box(5, 8, 31, 6, 10, 32, METAL).part(null);
      // Top rail and the built-in scope are sight gear: a fitted scope replaces them.
      b.gear(true);
      for (let z = 14; z < 34; z++) b.c(2, 12, 13, z, z + 1, z % 2 ? DARK : DARK_L);
      b.scope(15.5, 18, 34, 1.6, 13);
      b.gear(false);
      b.c(2, 6, 7, 28, 32, METAL_D);
      triggerGuard(b, 7, 29, 32);
      b.slant(2, 7, 1, 32, 3, 0.45, SEC);
      for (let z = 34; z < 44; z++) {
        const top = z < 36 ? 12 : 13;
        const bot = 7 - Math.floor((z - 34) * 0.4);
        b.c(4, bot, top, z, z + 1, z === 43 ? RUBBER : SEC);
      }
      b.carve(1, 8, 35, 5, 10, 39);
      // --- Fine hardware ---
      band(f, 2, 4, 9, 11, 14, 1, METAL_D);
      band(f, 2, 4, 9, 11, 22, 2, METAL_D);
      band(f, 2, 4, 9, 11, 36, 1, METAL_D);
      for (const hz of [1, 3]) f.c(4, 24, 25, hz, hz + 1, METAL_D);
      // Folded bipod under the fore-end: two legs and a pivot block.
      f.box(4, 10, 28, 5, 11, 40, METAL_D).box(7, 10, 28, 8, 11, 40, METAL_D).c(4, 9, 11, 38, 42, METAL_D);
      // Bolt knob (rides with the bolt).
      f.part('bolt').box(10, 13, 62, 12, 16, 64, BRASS).part(null);
      triggerBlade(f, 6, 30);
      sideFrame(f, 10, 16, 21, 56, 66, METAL_D);
      woodGrain(b, f, 1, 4, 7, 12, 13, 27, [SEC, SEC_L, SEC_D], 2);
      woodGrain(b, f, 1, 4, 3, 13, 34, 43, [SEC, SEC_L, SEC_D], 5);
      b.bevel();
      f.bevel();
      return {
        mount: { y: 11 },
        grip: [3, 3.5, 33.5],
        muzzle: [3, 10, 0],
        fore: [3, 6, 20],
        sight: [3, 15.5, 32],
        rig: rig('rifle', 'bolt', 'bolt'),
        parts: { bolt: { pivot: [3, 10, 31], dir: [0, 0, 1], axis: 'z' } },
      };
    },
  },

  pierce_rifle: {
    size: [8, 22, 48],
    build(b) {
      b.box(1, 7, 0, 7, 13, 5, SEC).box(0, 9, 1, 8, 11, 4, 0).box(1, 7, 0, 7, 8, 1, ACC).box(1, 12, 0, 7, 13, 1, ACC);
      b.box(3, 9, 0, 5, 11, 1, RUBBER);
      b.tube(4, 10, 5, 29, 1.6, METAL);
      for (let z = 6; z < 14; z++) b.box(3, 11, z, 5, 12, z + 1, z % 2 ? METAL_D : METAL);
      b.box(1, 7, 14, 7, 13, 29, PRI);
      for (let z = 16; z < 27; z += 3) b.symBox(1, 9, z, 2, 11, z + 2, DARK);
      b.rail(13, 14, 38);
      b.symBox(1, 5, 14, 2, 7, 25, METAL_D).symBox(1, 4, 24, 2, 5, 26, ACC);
      b.box(1, 6, 28, 7, 13, 38, PRI).sides(1, 11, 12, 29, 37, ACC);
      b.box(7, 10, 31, 8, 11, 34, METAL_D);
      b.scope(18, 22, 38, 2.2, 14);
      magazine(b, 6, 1, 29, 4, 0, SEC, ACC, 4);
      triggerGuard(b, 6, 33, 36);
      pistolGrip(b, 6, 0, 35, 3, DARK, 0.45);
      b.box(1, 6, 38, 7, 14, 48, PRI).carve(1, 8, 40, 7, 11, 45).box(1, 5, 47, 7, 15, 48, ACC);
      b.box(2, 14, 39, 6, 15, 45, SEC);
      b.bevel();
      return {
        stock: { y0: 14, z0: 22, z1: 38 },
        grip: [4, 3, 36.5],
        muzzle: [4, 10, 0],
        fore: [4, 6, 21],
        sight: [4, 18, 36],
        rig: rig('rifle', 'mag'),
        parts: { mag: { pivot: [4, 6, 31], dir: [0, -1, 0] } },
      };
    },
  },

  crossbow: {
    size: [26, 14, 32],
    build(b) {
      const cx = 13;
      b.box(11, 5, 4, 15, 9, 30, PRI).box(12, 8, 4, 14, 9, 24, DARK);
      for (let x = 0; x < 26; x++) {
        const dx = Math.abs(x + 0.5 - cx);
        if (dx < 1.5) continue;
        const z = 4 + Math.floor((dx * dx) / 45);
        b.box(x, 6, z, x + 1, 8, z + 2, dx > 11 ? ACC : SEC);
      }
      for (const x of [0, 24]) b.box(x, 5, 6, x + 2, 9, 9, METAL);
      for (let x = 1; x < 25; x++) {
        const t = Math.abs(x + 0.5 - cx) / 12;
        const z = Math.round(22 - t * 15);
        b.set(x, 8, z, DARK_L);
      }
      b.part('mag').box(12, 9, 3, 14, 10, 22, ACC).box(12, 8, 0, 14, 11, 3, METAL_L).box(12, 10, 0, 14, 11, 1, 0).box(12, 8, 0, 14, 9, 1, 0);
      b.box(12, 10, 18, 14, 11, 22, RED).box(11, 9, 19, 12, 10, 22, RED).box(14, 9, 19, 15, 10, 22, RED).part(null);
      b.tube(cx, 12, 14, 26, 1.6, DARK).tube(cx, 12, 14, 15, 1.4, LENS).set(12, 12, 14, LENS_HI).box(12, 9, 16, 14, 11, 17, ACC).box(12, 9, 23, 14, 11, 24, ACC);
      b.box(10, 2, 0, 16, 3, 1, METAL_D).box(10, 2, 0, 11, 6, 1, METAL_D).box(15, 2, 0, 16, 6, 1, METAL_D);
      triggerGuard(b, 5, 21, 24);
      pistolGrip(b, 5, 0, 23, 3, PRI, 0.4);
      b.box(11, 3, 28, 15, 10, 32, PRI).box(11, 3, 31, 15, 10, 32, DARK);
      b.bevel();
      return {
        stock: { y0: 11, z0: 14, z1: 27 },
        grip: [cx, 2.5, 24.5],
        muzzle: [cx, 9.5, 0],
        fore: [cx, 5, 13],
        sight: [cx, 12, 24],
        rig: rig('rifle', 'crossbow'),
        parts: { mag: { pivot: [cx, 9.5, 11], dir: [0, 0, -1] } },
      };
    },
  },

  // Builder tool ----------------------------------------------------------------------------
  block_gun: {
    size: [10, 18, 26],
    build(b) {
      b.box(2, 5, 0, 8, 11, 4, SEC).box(3, 6, 0, 7, 10, 2, 0).box(3, 6, 2, 7, 10, 3, DARK_L);
      b.box(1, 4, 3, 9, 12, 16, PRI);
      for (let z = 4; z < 15; z += 3) b.box(0, 6, z, 1, 8, z + 1, SEC).box(9, 6, z, 10, 8, z + 1, SEC);
      b.box(2, 12, 5, 8, 13, 13, SEC);
      b.box(3, 13, 6, 7, 17, 12, ACC);
      for (const [y, z0, z1] of [[14, 6, 12], [16, 6, 12]] as const) b.box(3, y, z0, 7, y + 1, z1, ACC_D);
      b.box(3, 13, 8, 7, 14, 9, ACC_D).box(3, 15, 10, 7, 16, 11, ACC_D);
      b.box(1, 9, 3, 9, 10, 16, ACC);
      b.box(4, 0, 12, 6, 4, 16, SEC).box(4, 0, 13, 6, 1, 16, DARK_L);
      b.box(3, 5, 16, 7, 10, 26, SEC).box(3, 4, 25, 7, 11, 26, DARK);
      b.bevel();
      return {
        grip: [5, 2, 14],
        muzzle: [5, 8, 0],
        fore: [5, 4, 7],
        sight: [5, 13, 13],
        rig: rig('rifle', 'none'),
      };
    },
  },
};

/** Archetype fallbacks for specs that do not belong to a known weapon. */
export const ARCHETYPE_DESIGN: Record<string, string> = {
  pistol: 'peashooter',
  revolver: 'hand_cannon',
  smg: 'buzz_smg',
  rifle: 'brick_rifle',
  carbine: 'pulse_carbine',
  shotgun: 'scattergun',
  lmg: 'thumper_lmg',
  minigun: 'voxel_shredder',
  sniper: 'longshot',
  dmr: 'ranger_br',
  railgun: 'railgun',
  rocket: 'rocket_launcher',
  grenadelauncher: 'bouncer',
  flamethrower: 'dragons_breath',
  laser: 'photon_pistol',
  plasma: 'plasma_cannon',
  tesla: 'tesla_coil',
  nailgun: 'nail_driver',
  mininglaser: 'mining_laser',
  blockgun: 'block_gun',
  hammer: 'builders_hammer',
  sword: 'plasma_blade',
  pickaxe: 'pickaxe',
  axe: 'frying_pan',
  katana: 'katana',
  buzzblade: 'buzz_blade',
  flare: 'flare_gun',
  crossbow: 'crossbow',
};
